import * as MuiIcons from "@mui/icons-material";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { type ElementType, type ReactElement } from "react";

const muiIconExports = MuiIcons as Record<string, unknown>;

const legacyActionIconNames: Record<string, string> = {
  add: "Add",
  edit: "Edit",
  delete: "Delete",
  play: "PlayArrow",
  stop: "Stop",
  check: "Check",
  close: "Close",
  refresh: "Refresh",
  save: "Save",
};

const isRenderableIconExport = (value: unknown): value is ElementType => {
  return (
    typeof value === "function" || (typeof value === "object" && value !== null)
  );
};

export const muiIconNames = Object.freeze(
  Object.keys(muiIconExports)
    .filter((name) => {
      if (!/^[A-Z]/.test(name)) return false;
      return isRenderableIconExport(muiIconExports[name]);
    })
    .sort((a, b) => a.localeCompare(b)),
);

export function normalizeMuiIconName(iconName?: string | null): string | null {
  const trimmed = iconName?.trim();
  if (!trimmed || trimmed === "none") return null;
  return legacyActionIconNames[trimmed] ?? trimmed;
}

export function resolveMuiIconComponent(
  iconName?: string | null,
): ElementType<SvgIconProps> | null {
  const normalized = normalizeMuiIconName(iconName);
  if (!normalized) return null;

  const Icon = muiIconExports[normalized];
  return isRenderableIconExport(Icon)
    ? (Icon as ElementType<SvgIconProps>)
    : null;
}

export function renderMuiIcon(
  iconName?: string | null,
  props?: SvgIconProps,
): ReactElement | undefined {
  const Icon = resolveMuiIconComponent(iconName);
  return Icon ? <Icon fontSize="small" {...props} /> : undefined;
}

export function MuiIconSelect({
  label,
  noneLabel,
  value,
  onChange,
  size = "small",
  sx,
}: {
  label: string;
  noneLabel?: string;
  value?: string | null;
  onChange: (value: string | null) => void;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
}) {
  const selectedValue = normalizeMuiIconName(value);

  return (
    <Autocomplete
      size={size}
      options={muiIconNames}
      value={
        selectedValue && muiIconNames.includes(selectedValue)
          ? selectedValue
          : null
      }
      onChange={(_, nextValue) => onChange(nextValue)}
      autoHighlight
      clearText={noneLabel}
      noOptionsText="Aucune icône"
      sx={sx}
      renderOption={(optionProps, option) => {
        const Icon = resolveMuiIconComponent(option);
        const { key, ...restOptionProps } = optionProps;
        return (
          <Box
            component="li"
            key={key}
            {...restOptionProps}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {Icon ? <Icon fontSize="small" /> : null}
            <Typography variant="body2">{option}</Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={noneLabel}
          size={size}
        />
      )}
    />
  );
}
